import { useEffect, useMemo, useState } from "react";
import {
  QrCode,
  Search,
  Download,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Layers,
  CircleDot,
  Circle,
  Building2,
  Activity,
  MapPin,
} from "lucide-react";
import { HospitalSelector } from "../../components/dashboard/HospitalSelector";
import { useStickyHospital } from "../../hooks/useStickyHospital";
import { CheckpointHistory, type CheckpointSummary } from "../../components/CheckpointHistory";
import { useAuthStore } from "../../state/auth";
import { useTranslation, type LangCode } from "../../i18n";
import { cn } from "../../lib/utils";

type TFn = (key: string, fallback?: string) => string;

// A checkpoint is considered "active" when it has been scanned within this window.
const ACTIVE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

interface DirectoryCheckpoint {
  id: string;
  name: string;
  code: string | null;
  building: string | null;
  floor: string | null;
  section: string | null;
  wing: string | null;
  hospital_id: string;
  hospital_name: string | null;
  total_scan_count: number;
  last_scanned: string | null;
}

// A checkpoint plus its normalized floor (see normalizeFloor).
interface EnrichedCheckpoint extends DirectoryCheckpoint {
  floorKey: string;
  floorOrder: number;
  floorLabel: string;
}

function isActive(cp: DirectoryCheckpoint): boolean {
  if (!cp.last_scanned) return false;
  return Date.now() - new Date(cp.last_scanned).getTime() <= ACTIVE_WINDOW_MS;
}

export function CheckpointDirectoryPage() {
  const { t, lang } = useTranslation();
  const { user, token } = useAuthStore();

  const isAdmin = user?.role === "ADMIN" || user?.role === "Administrator";
  const isDirector = user?.role === "DIRECTOR";
  const canSelectHospital = isAdmin || isDirector || user?.role === "SUPERVISOR";

  const [selectedHospitalId, setSelectedHospitalId] = useStickyHospital();
  const [checkpoints, setCheckpoints] = useState<DirectoryCheckpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState<CheckpointSummary | null>(null);

  // Drill-down position: Hospitals → this hospital's Floors → this floor's Checkpoints.
  const [openHospital, setOpenHospital] = useState<string | null>(null);
  const [openFloor, setOpenFloor] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.email) return;
      setLoading(true);
      setError(null);
      try {
        const url = new URL("/api/checkpoints", window.location.origin);
        if (selectedHospitalId) url.searchParams.append("hospital_id", selectedHospitalId);
        const headers: Record<string, string> = { "x-user-email": user.email, Accept: "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const res = await fetch(url.toString(), { headers, cache: "no-store" });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Request failed (${res.status})`);
        }
        const json = await res.json();
        if (!cancelled) setCheckpoints(json.checkpoints || []);
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message || "Failed to load checkpoints");
          setCheckpoints([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [selectedHospitalId, user?.email, token]);

  // Normalize each checkpoint's floor once. "Floor 1", "1F", "L1", "1st" all
  // collapse to the same floor node so the directory shows one "1st Floor".
  const enriched: EnrichedCheckpoint[] = useMemo(
    () =>
      checkpoints.map((cp) => {
        const f = normalizeFloor(cp.floor);
        return { ...cp, floorKey: f.key, floorOrder: f.order, floorLabel: f.label };
      }),
    [checkpoints]
  );

  // Hospitals (top level of the tree), each with its checkpoints.
  const hospitals = useMemo(() => {
    const m = new Map<string, { id: string; name: string; cps: EnrichedCheckpoint[] }>();
    for (const cp of enriched) {
      const id = cp.hospital_id || "~unknown";
      if (!m.has(id)) m.set(id, { id, name: cp.hospital_name || t("checkpoints.unknownHospital", "Unknown Hospital"), cps: [] });
      m.get(id)!.cps.push(cp);
    }
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enriched]);

  // Reset the drill-down when the underlying data or hospital filter changes.
  useEffect(() => {
    setOpenFloor(null);
    // With a single hospital in scope, skip the redundant hospital step and open
    // straight to its floors; with several, start at the hospital list.
    setOpenHospital(hospitals.length === 1 ? hospitals[0].id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitals.length, selectedHospitalId]);

  const searching = search.trim().length > 0;
  const term = search.trim().toLowerCase();

  // Flat search across every hospital & floor — searching bypasses the tree.
  const searchResults = useMemo(() => {
    if (!searching) return [];
    return enriched
      .filter(
        (cp) =>
          cp.name.toLowerCase().includes(term) ||
          (cp.code || "").toLowerCase().includes(term) ||
          (cp.building || "").toLowerCase().includes(term) ||
          (cp.hospital_name || "").toLowerCase().includes(term)
      )
      .sort((a, b) => (a.hospital_name || "").localeCompare(b.hospital_name || "") || a.floorOrder - b.floorOrder || a.name.localeCompare(b.name));
  }, [enriched, searching, term]);

  const activeHospital = openHospital ? hospitals.find((h) => h.id === openHospital) ?? null : null;

  // Floors within the open hospital.
  const floors = useMemo(() => {
    if (!activeHospital) return [];
    const m = new Map<string, { key: string; order: number; label: string; cps: EnrichedCheckpoint[] }>();
    for (const cp of activeHospital.cps) {
      if (!m.has(cp.floorKey)) m.set(cp.floorKey, { key: cp.floorKey, order: cp.floorOrder, label: cp.floorLabel, cps: [] });
      m.get(cp.floorKey)!.cps.push(cp);
    }
    return Array.from(m.values()).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  }, [activeHospital]);

  const activeFloor = openFloor ? floors.find((f) => f.key === openFloor) ?? null : null;

  // Overall summary (unfiltered by the drill-down position).
  const stats = useMemo(() => {
    const floorKeys = new Set<string>();
    let activeCount = 0;
    for (const cp of enriched) {
      floorKeys.add(cp.floorKey);
      if (isActive(cp)) activeCount++;
    }
    return { total: enriched.length, hospitals: hospitals.length, floors: floorKeys.size, active: activeCount };
  }, [enriched, hospitals.length]);

  const openCheckpoint = (cp: DirectoryCheckpoint) =>
    setActive({ id: cp.id, name: cp.name, code: cp.code, building: cp.building, floor: cp.floor, hospital_name: cp.hospital_name });

  const exportCsv = () => {
    const header = [
      t("checkpoints.colHospital", "Hospital"),
      t("checkpoints.colCode", "Code"),
      t("checkpoints.colName", "Name"),
      t("checkpoints.colBuilding", "Building"),
      t("checkpoints.colFloor", "Floor"),
      t("checkpoints.colLastScanned", "Last Scanned"),
      t("checkpoints.colTotalScans", "Total Scans"),
      t("checkpoints.colStatus", "Status"),
    ];
    const rows = enriched.map((cp) => [
      cp.hospital_name || "",
      cp.code || "",
      cp.name,
      cp.building || "",
      cp.floorLabel,
      cp.last_scanned ? new Date(cp.last_scanned).toISOString() : "",
      String(cp.total_scan_count),
      isActive(cp) ? t("checkpoints.active", "Active") : t("checkpoints.inactive", "Inactive"),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `checkpoints-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Whether the "back to all hospitals" crumb should do anything (only when the
  // scope actually has more than one hospital to return to).
  const multiHospital = hospitals.length > 1;

  return (
    <div className="space-y-6 p-6 max-w-[1400px] mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-2xl font-bold text-gray-900 dark:text-white">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#3B7BBE]/10 text-[#3B7BBE]">
              <QrCode className="h-5 w-5" />
            </span>
            {t("checkpoints.directoryTitle", "Checkpoint Directory")}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {t("checkpoints.directorySubtitleTree", "Browse by hospital, then floor, down to each checkpoint.")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {canSelectHospital && (
            <HospitalSelector selectedHospitalId={selectedHospitalId} onHospitalChange={setSelectedHospitalId} />
          )}
          <button
            type="button"
            onClick={exportCsv}
            disabled={enriched.length === 0}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-95 disabled:opacity-40 dark:border-[#33334d] dark:bg-[#24243e] dark:text-gray-200 dark:hover:bg-[#2a2a47]"
          >
            <Download className="h-4 w-4 text-[#3B7BBE]" />
            {t("checkpoints.exportCsv", "Export CSV")}
          </button>
        </div>
      </header>

      {/* Summary overview */}
      {!loading && !error && stats.total > 0 && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile icon={<QrCode className="h-4 w-4" />} label={t("checkpoints.statTotal", "Checkpoints")} value={stats.total.toLocaleString(lang)} />
          <StatTile icon={<Building2 className="h-4 w-4" />} label={t("checkpoints.statHospitals", "Hospitals")} value={stats.hospitals.toLocaleString(lang)} />
          <StatTile icon={<Layers className="h-4 w-4" />} label={t("checkpoints.statFloors", "Floors")} value={stats.floors.toLocaleString(lang)} />
          <StatTile icon={<Activity className="h-4 w-4" />} label={t("checkpoints.statScanned30d", "Scanned in last 30 days")} value={stats.active.toLocaleString(lang)} accent="emerald" />
        </div>
      )}

      {/* Search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("checkpoints.searchPlaceholderTree", "Search all checkpoints by name, code, building, or hospital…")}
            className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 py-2.5 text-sm text-gray-700 shadow-sm outline-none focus:border-[#3B7BBE] focus:ring-2 focus:ring-[#3B7BBE]/30 dark:border-[#33334d] dark:bg-[#24243e] dark:text-gray-200"
          />
        </div>
      </div>

      {/* Breadcrumb (hidden while searching) */}
      {!searching && !loading && !error && stats.total > 0 && (
        <nav className="flex flex-wrap items-center gap-1.5 text-sm">
          <Crumb
            label={t("checkpoints.crumbAll", "All Hospitals")}
            icon={<Building2 className="h-4 w-4" />}
            onClick={multiHospital ? () => { setOpenHospital(null); setOpenFloor(null); } : undefined}
            active={!activeHospital}
          />
          {activeHospital && (
            <>
              <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-600" />
              <Crumb label={activeHospital.name} onClick={activeFloor ? () => setOpenFloor(null) : undefined} active={!activeFloor} />
            </>
          )}
          {activeFloor && (
            <>
              <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-600" />
              <Crumb label={activeFloor.label} active />
            </>
          )}
        </nav>
      )}

      {/* Body */}
      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-gray-500">
          <RefreshCw className="h-8 w-8 animate-spin text-[#3B7BBE]" />
          <p className="text-sm">{t("checkpoints.loadingDirectory", "Loading checkpoints…")}</p>
        </div>
      ) : error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center text-sm font-medium text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
          {error}
        </div>
      ) : stats.total === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-24 text-center text-gray-500">
          <QrCode className="h-10 w-10 text-gray-300" />
          <p className="text-sm">{t("checkpoints.empty", "No checkpoints found for this hospital.")}</p>
        </div>
      ) : searching ? (
        /* ---- Search results (flat) ---- */
        searchResults.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-gray-500">
            <QrCode className="h-10 w-10 text-gray-300" />
            <p className="text-sm">{t("checkpoints.noMatch", "No checkpoints match your search.")}</p>
          </div>
        ) : (
          <div>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t("checkpoints.resultsCount", "{n} matching checkpoints").replace("{n}", String(searchResults.length))}
            </p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {searchResults.map((cp) => (
                <button
                  key={cp.id}
                  type="button"
                  onClick={() => openCheckpoint(cp)}
                  className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition-all hover:border-[#3B7BBE]/40 hover:shadow active:scale-[0.99] dark:border-[#33334d] dark:bg-[#24243e]"
                >
                  <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#3B7BBE]/10 text-[#3B7BBE]">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-gray-900 dark:text-gray-100">{cp.name}</span>
                    <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                      {cp.hospital_name || "—"} · {cp.floorLabel}
                      {cp.building ? ` · ${cp.building}` : ""}
                    </span>
                  </span>
                  <StatusDot active={isActive(cp)} />
                </button>
              ))}
            </div>
          </div>
        )
      ) : !activeHospital ? (
        /* ---- Level 0: Hospitals ---- */
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {hospitals.map((h) => {
            const floorCount = new Set(h.cps.map((c) => c.floorKey)).size;
            const activeCount = h.cps.filter(isActive).length;
            return (
              <BranchCard
                key={h.id}
                icon={<Building2 className="h-5 w-5" />}
                title={h.name}
                sub={
                  `${t("checkpoints.nCheckpoints", "{n} checkpoints").replace("{n}", String(h.cps.length))}` +
                  ` · ${t("checkpoints.nFloors", "{n} floors").replace("{n}", String(floorCount))}`
                }
                activeCount={activeCount}
                onClick={() => { setOpenHospital(h.id); setOpenFloor(null); }}
              />
            );
          })}
        </div>
      ) : !activeFloor ? (
        /* ---- Level 1: Floors within the hospital ---- */
        <div className="space-y-3">
          {multiHospital && (
            <BackButton label={t("checkpoints.backHospitals", "All hospitals")} onClick={() => { setOpenHospital(null); setOpenFloor(null); }} t={t} />
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {floors.map((f) => {
              const activeCount = f.cps.filter(isActive).length;
              return (
                <BranchCard
                  key={f.key}
                  icon={<Layers className="h-5 w-5" />}
                  title={f.label}
                  sub={t("checkpoints.nCheckpoints", "{n} checkpoints").replace("{n}", String(f.cps.length))}
                  activeCount={activeCount}
                  onClick={() => setOpenFloor(f.key)}
                />
              );
            })}
          </div>
        </div>
      ) : (
        /* ---- Level 2: Checkpoints on the floor ---- */
        <div className="space-y-3">
          <BackButton label={activeHospital.name} onClick={() => setOpenFloor(null)} t={t} />
          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-[#33334d] dark:bg-[#24243e]">
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 dark:border-[#33334d]">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#3B7BBE]/10 text-[#3B7BBE]">
                <Layers className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900 dark:text-white">{activeFloor.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{activeHospital.name}</div>
              </div>
              <span className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-600 dark:bg-[#33334d] dark:text-gray-300">
                {activeFloor.cps.length}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 dark:bg-[#1f1f38] dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">{t("checkpoints.colCode", "Code")}</th>
                    <th className="px-4 py-2 text-left font-semibold">{t("checkpoints.colName", "Name")}</th>
                    <th className="px-4 py-2 text-left font-semibold">{t("checkpoints.colBuilding", "Building")}</th>
                    <th className="px-4 py-2 text-left font-semibold">{t("checkpoints.colLastScanned", "Last Scanned")}</th>
                    <th className="px-4 py-2 text-right font-semibold">{t("checkpoints.colTotalScans", "Total Scans")}</th>
                    <th className="px-4 py-2 text-left font-semibold">{t("checkpoints.colStatus", "Status")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-[#2a2a47]">
                  {activeFloor.cps
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((cp) => (
                      <tr
                        key={cp.id}
                        onClick={() => openCheckpoint(cp)}
                        className="cursor-pointer transition-colors hover:bg-[#3B7BBE]/5 dark:hover:bg-[#2a2a47]"
                      >
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-500 dark:text-gray-400">{cp.code || "—"}</td>
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">{cp.name}</td>
                        <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{cp.building || "—"}</td>
                        <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                          {cp.last_scanned ? formatDate(cp.last_scanned, lang) : t("checkpoints.never", "Never")}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-gray-900 dark:text-gray-100">
                          {cp.total_scan_count.toLocaleString(lang)}
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusPill active={isActive(cp)} t={t} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {active && <CheckpointHistory checkpoint={active} onClose={() => setActive(null)} />}
    </div>
  );
}

/* ── pieces ─────────────────────────────────────────────────────────────── */

function Crumb({ label, icon, onClick, active }: { label: string; icon?: React.ReactNode; onClick?: () => void; active?: boolean }) {
  const base = "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-semibold";
  if (active || !onClick) {
    return <span className={cn(base, "bg-[#3B7BBE]/10 text-[#3B7BBE]")}>{icon}{label}</span>;
  }
  return (
    <button type="button" onClick={onClick} className={cn(base, "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2a2a47]")}>
      {icon}
      {label}
    </button>
  );
}

function BranchCard({
  icon,
  title,
  sub,
  activeCount,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  sub: string;
  activeCount: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition-all hover:border-[#3B7BBE]/50 hover:shadow-md active:scale-[0.99] dark:border-[#33334d] dark:bg-[#24243e] dark:hover:border-[#3B7BBE]/50"
    >
      <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#3B7BBE]/10 text-[#3B7BBE]">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold text-gray-900 dark:text-white">{title}</span>
        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
          {sub}
          {activeCount > 0 && <span className="text-emerald-600 dark:text-emerald-400"> · {activeCount} active</span>}
        </span>
      </span>
      <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-[#3B7BBE] dark:text-gray-600" />
    </button>
  );
}

function BackButton({ label, onClick, t }: { label: string; onClick: () => void; t: TFn }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2a2a47]"
    >
      <ChevronLeft className="h-4 w-4" />
      {t("checkpoints.back", "Back to")} {label}
    </button>
  );
}

function StatTile({
  icon,
  label,
  value,
  accent = "blue",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent?: "blue" | "emerald";
}) {
  const accentClass =
    accent === "emerald" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-[#3B7BBE]/10 text-[#3B7BBE]";
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-[#33334d] dark:bg-[#24243e]">
      <span className={cn("flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg", accentClass)}>{icon}</span>
      <div className="min-w-0">
        <div className="text-xl font-bold leading-tight text-gray-900 dark:text-white">{value}</div>
        <div className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
      </div>
    </div>
  );
}

function StatusPill({ active, t }: { active: boolean; t: TFn }) {
  return active ? (
    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
      <CircleDot className="h-3 w-3" />
      {t("checkpoints.active", "Active")}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2.5 py-1 text-[11px] font-bold text-gray-500 dark:bg-[#33334d] dark:text-gray-400">
      <Circle className="h-3 w-3" />
      {t("checkpoints.inactive", "Inactive")}
    </span>
  );
}

function StatusDot({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        "mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full",
        active ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"
      )}
    />
  );
}

/* ── floor normalization ────────────────────────────────────────────────── */

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Canonical, human floor label derived purely from the floor order, so every
// raw variant that maps to the same order shows one consistent name.
function canonicalFloorLabel(order: number): string {
  if (order === 0) return "Ground Floor";
  if (order === 0.5) return "Mezzanine";
  if (order === 9000) return "Penthouse";
  if (order < 0) return order === -1 ? "Lower Level" : `Basement ${-order}`;
  return `${ordinal(order)} Floor`;
}

/**
 * Collapse the many ways a floor gets written — "Floor 1", "1F", "F1", "L1",
 * "Level 1", "1st", "1" — into a single normalized floor node. The `floor`
 * column is a dedicated field, so any embedded number is taken as the floor.
 * Words (Ground, Lobby, LL, Basement, Mezzanine, Penthouse) get sensible
 * orders; anything unrecognized keeps its own label.
 */
function normalizeFloor(raw: string | null | undefined): { key: string; order: number; label: string } {
  if (raw == null || String(raw).trim() === "") return { key: "~unassigned", order: 100000, label: "Unassigned" };
  const s = String(raw).trim();
  const low = s.toLowerCase();
  const mk = (order: number) => ({ key: `f${order}`, order, label: canonicalFloorLabel(order) });

  if (/^(g|gf|ground(\s*floor)?|lobby|main(\s*floor)?)$/.test(low)) return mk(0);
  if (/^(ll|lower\s*level|lower)$/.test(low)) return mk(-1);
  if (/^(m|mez+|mezzanine)$/.test(low)) return mk(0.5);
  if (/^(ph|penthouse|roof(top)?)$/.test(low)) return mk(9000);

  const mb = low.match(/^b(?:asement)?\s*-?\s*(\d+)$/);
  if (mb) return mk(-Number(mb[1]));

  const m = low.match(/-?\d+/);
  if (m) {
    const n = Number(m[0]);
    return mk(n);
  }

  // Unrecognized textual label (e.g. "Annex", "Tower A") — its own node,
  // sorted after the numbered floors but before "Unassigned".
  return { key: `u:${low}`, order: 50000, label: s };
}

function formatDate(ts: string, lang: LangCode) {
  return new Intl.DateTimeFormat(lang, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(ts));
}
